import React from "react";
import type { PatientModel } from "@/apidtos";

const STORAGE_KEY = "spg_ordering_for_patient_id";

interface SelectedPatientContextValue {
  patientId: number | null;
  patient: PatientModel | null;
  setPatient: (patient: PatientModel | null) => void;
  setPatientId: (id: number | null) => void;
  /** All known patients for the current provider user (or empty). */
  patients: PatientModel[];
  setPatients: (patients: PatientModel[]) => void;
}

const SelectedPatientContext = React.createContext<SelectedPatientContextValue | null>(null);

function readStoredId(): number | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function writeStoredId(id: number | null) {
  if (typeof window === "undefined") return;
  if (id === null) window.localStorage.removeItem(STORAGE_KEY);
  else window.localStorage.setItem(STORAGE_KEY, String(id));
}

export const SelectedPatientProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [patientId, setPatientIdState] = React.useState<number | null>(null);
  const [patients, setPatients] = React.useState<PatientModel[]>([]);
  /** Rows pinned from outside the header list (e.g. Patients grid begin-order); survives provider list refresh. */
  const [patientSnapshots, setPatientSnapshots] = React.useState<PatientModel[]>([]);

  // Hydrate from localStorage after mount to avoid SSR mismatch.
  React.useEffect(() => {
    setPatientIdState(readStoredId());
  }, []);

  // Cross-tab sync.
  React.useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setPatientIdState(readStoredId());
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const setPatientId = React.useCallback((id: number | null) => {
    writeStoredId(id);
    setPatientIdState(id);
  }, []);

  const setPatient = React.useCallback((p: PatientModel | null) => {
    const id = p?.Id ?? null;
    setPatientSnapshots((prev) => {
      if (!p?.Id) return prev;
      return prev.some((x) => x.Id === p.Id) ? prev : [...prev, p];
    });
    setPatientId(id);
  }, [setPatientId]);

  const patient = React.useMemo(() => {
    if (!patientId) return null;
    const fromPrimary = patients.find((p) => p.Id === patientId);
    if (fromPrimary) return fromPrimary;
    return patientSnapshots.find((p) => p.Id === patientId) ?? null;
  }, [patientId, patients, patientSnapshots]);

  const value = React.useMemo<SelectedPatientContextValue>(
    () => ({ patientId, patient, setPatient, setPatientId, patients, setPatients }),
    [patientId, patient, setPatient, setPatientId, patients, setPatients],
  );

  return (
    <SelectedPatientContext.Provider value={value}>
      {children}
    </SelectedPatientContext.Provider>
  );
};

/**
 * Returns the currently-selected patient (the "Ordering for" choice).
 * Safe to call outside the provider — returns a no-op default.
 */
export function useSelectedPatient(): SelectedPatientContextValue {
  const ctx = React.useContext(SelectedPatientContext);
  if (!ctx) {
    return {
      patientId: null,
      patient: null,
      setPatient: () => {},
      setPatientId: () => {},
      patients: [],
      setPatients: () => {},
    };
  }
  return ctx;
}
